/**
 * When a user navigates to a results page from the CrossSell carousel, the
 * router state contains { fromCrossSell: true, crossSellStorageKey }.
 * This helper reads all quotes for that product from localStorage so the
 * results page can display them without making a new API call.
 *
 * The CrossSell API returns quotes in the same structure as the product quote
 * API, so the results page renders them identically.
 */
export function getCrossSellQuoteDetails(locationState) {
  if (!locationState?.fromCrossSell || !locationState?.crossSellStorageKey) {
    return null;
  }

  try {
    const raw = localStorage.getItem(locationState.crossSellStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.QuoteDetails ?? null;
  } catch {
    return null;
  }
}

export function getCrossSellQuotesForResults(locationState) {
  if (!locationState?.fromCrossSell || !locationState?.crossSellStorageKey) {
    return null;
  }

  try {
    const raw = localStorage.getItem(locationState.crossSellStorageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const quotes = Array.isArray(parsed)
      ? parsed
      : Array.isArray(parsed?.Quotes)
        ? parsed.Quotes
        : [];

    if (quotes.length === 0) return null;

    // Apply ranking so results pages render bestDeal / top-3 treatments correctly.
    const sorted = [...quotes].sort(
      (a, b) =>
        Number(a.TotalPremium ?? a.DisplayAmount ?? 0) -
        Number(b.TotalPremium ?? b.DisplayAmount ?? 0)
    );
    const top3Ids = new Set(sorted.slice(0, 3).map((q) => q.QuotePK));
    const bestId = sorted[0]?.QuotePK;

    return quotes.map((quote) => ({
      ...quote,
      _rank: sorted.findIndex((q) => q.QuotePK === quote.QuotePK) + 1,
      bestDeal: quote.QuotePK === bestId,
      isTop3: top3Ids.has(quote.QuotePK),
    }));
  } catch {
    return null;
  }
}
